import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';
import { useAppConfig } from '../config/AppConfigContext';

type AccessTokenClaims = Record<string, unknown>;

/**
 * Resolve the Auth0 roles claim key using the configured API audience.
 */
const getRolesClaimKey = (audience?: string): string | null => {
  if (!audience) {
    return null;
  }

  const normalizedAudience = audience.endsWith('/') ? audience.slice(0, -1) : audience;
  return normalizedAudience.endsWith('/roles') ? normalizedAudience : `${normalizedAudience}/roles`;
};

/**
 * Extract roles from an Auth0 access token.
 */
export const getRolesFromClaims = (claims: AccessTokenClaims, audience?: string): string[] => {
  const rolesClaimKey = getRolesClaimKey(audience);
  if (!rolesClaimKey) {
    return [];
  }

  const roles = claims[rolesClaimKey];

  if (Array.isArray(roles)) {
    return roles.filter((role): role is string => typeof role === 'string');
  }

  if (typeof roles === 'string') {
    return [roles];
  }

  return [];
};

/** The single role a badge should be shown for, when a user has more than one. */
export type PartnerDisplayRole = 'AceUpAdmin' | 'AceUpViewer' | 'PartnerAdmin' | 'PartnerViewer';

/**
 * Pick the role to badge when a token carries more than one — AceUp staff
 * roles outrank partner roles (an AceUp admin reviewing partner data is
 * still AceUp staff first), and within a tier, admin outranks viewer.
 *
 * Exported because the same precedence applies to *other* members' roles as
 * served by `GET /partner/team` — the backend deliberately sends the label
 * array and leaves this decision in one place, here.
 */
export const getDisplayRole = (roles: string[]): PartnerDisplayRole | null => {
  if (roles.includes('AceUpAdmin')) return 'AceUpAdmin';
  if (roles.includes('AceUpViewer')) return 'AceUpViewer';
  if (roles.includes('PartnerAdmin')) return 'PartnerAdmin';
  if (roles.includes('PartnerViewer')) return 'PartnerViewer';
  return null;
};

/** Whether a set of role names belongs to the AceUp-staff family (BR-002). */
const isAceUpRole = (roles: string[]): boolean =>
  roles.includes('AceUpAdmin') || roles.includes('AceUpViewer');

/**
 * Whether the caller and a target share a role family (BR-002) — the one
 * check consumers actually need, so the family comparison lives in exactly
 * one place instead of being reconstructed (and possibly inverted) at every
 * call site. `callerIsAceUpStaff` is `usePartnerRoles()`'s own `isAceUpStaff`
 * for the signed-in user; `targetRoles` is another member's or invitation's
 * roles as served by `GET /partner/team` / `GET /partner/team/invitations`.
 * Ally rejects managing an out-of-family target with a `404` regardless —
 * this is UI-only defense-in-depth so the dashboard never offers an action
 * it already knows will fail.
 */
export const isSameRoleFamily = (callerIsAceUpStaff: boolean, targetRoles: string[]): boolean =>
  callerIsAceUpStaff === isAceUpRole(targetRoles);

export const usePartnerRoles = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const { auth0Audience } = useAppConfig();
  const [roles, setRoles] = useState<string[]>([]);
  // Default to "loading" whenever the user is authenticated so that consumers
  // (e.g. AdminRoute) don't briefly observe `isAdmin === false` on the first
  // render, before the role-loading effect has had a chance to run.
  const [isLoading, setIsLoading] = useState<boolean>(isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      setRoles([]);
      setIsLoading(false);
      // Explicit, so every branch of this effect returns a value and the
      // cleanup below is not mistaken for an inconsistent return.
      return undefined;
    }

    let isMounted = true;
    // Mark as loading synchronously here too — covers the case where
    // `isAuthenticated` flips from false to true after mount.
    setIsLoading(true);

    const loadRoles = async () => {
      try {
        const accessToken = auth0Audience
          ? await getAccessTokenSilently({ authorizationParams: { audience: auth0Audience } })
          : await getAccessTokenSilently();
        const claims = jwtDecode<AccessTokenClaims>(accessToken);

        if (isMounted) {
          setRoles(getRolesFromClaims(claims, auth0Audience));
        }
      } catch {
        if (isMounted) {
          setRoles([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadRoles();

    return () => {
      isMounted = false;
    };
  }, [auth0Audience, getAccessTokenSilently, isAuthenticated]);

  const isAceUpStaff = isAceUpRole(roles);
  const isAdmin = roles.includes('AceUpAdmin') || roles.includes('PartnerAdmin');
  const isPartnerRole = roles.includes('PartnerAdmin') || roles.includes('PartnerViewer');
  const displayRole = getDisplayRole(roles);

  return {
    roles,
    isAdmin,
    isAceUpStaff,
    isLoading,
    isPartnerRole,
    displayRole,
  };
};

export default usePartnerRoles;
